// src/business/pages/procurement/PurchaseRequisitions.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, ChevronDown, Info, Loader2, Package, Plus, Search } from 'lucide-react';
import Swal from "../../../utils/apparifySwal";
import { procurementAPI, productsAPI, suppliersAPI } from '../../services/businessErpService';
import { PageHeader, DataTable, Pagination, StatusBadge, FormModal, BtnPrimary, BtnSecondary } from '../../components/erp';
import Panel from '../../components/Panel';
import { businessSwalBase } from '../../utils/businessSwal';
import { getPRLabel } from '../../../utils/documentLabels';
import ShopSelect from '../../../components/ShopSelect';
import {
  VariantList,
  SummaryPanel,
  REASON_PRESETS,
  calcSummary,
  getVariantDefaultRequestQty,
  getVariantLabel,
  getVariantStockState,
  normalizeRequestedQty,
  reasonToText,
  selectedMap,
} from '../../components/procurement';

const inputCls =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100';

const PROCUREMENT_STATUSES = ['DRAFT', 'LISTED'];

const PR_STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'PARTIALLY_USED_FOR_RFQ', label: 'Partially Used for RFQ' },
  { value: 'FULLY_USED_FOR_RFQ', label: 'Fully Used for RFQ' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'DRAFT', label: 'Draft' },
];

const MAX_INLINE_VARIANTS = 3;

const COLUMNS = [
  { key: 'pr_number', label: 'PR #', sortable: true, className: 'font-mono text-xs', render: (r) => getPRLabel(r) },
  { key: 'product_name', label: 'Product', sortable: true },
  {
    key: 'variant_label',
    label: 'Variants',
    render: (r) => {
      const count = r.item_count || 0;
      if (!count) return <span className="text-neutral-400">Product-level</span>;
      if (count <= MAX_INLINE_VARIANTS) {
        return <span className="text-xs">{r.variant_label || `${count} variant(s)`}</span>;
      }
      return <VariantSummaryCell count={count} label={r.variant_label} />;
    },
  },
  { key: 'requested_quantity', label: 'Qty', sortable: true, className: 'tabular-nums' },
  { key: 'reason', label: 'Reason', render: (r) => <span className="max-w-[12rem] truncate block" title={r.reason || ''}>{r.reason || '-'}</span> },
  { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  {
    key: 'requested_by_name',
    label: 'Requester',
    render: (r) => r.requested_by_name || '-',
  },
  {
    key: 'created_at',
    label: 'Created',
    sortable: true,
    render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'),
  },
  { key: 'actions', label: '', render: (r) => r._actions },
];

function VariantSummaryCell({ count, label }) {
  const [expanded, setExpanded] = useState(false);
  const names = (label || '').split(', ').filter(Boolean);
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
      >
        {count} variant{count !== 1 ? 's' : ''}
        <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && names.length > 0 && (
        <ul className="mt-1.5 max-h-32 space-y-0.5 overflow-y-auto rounded-lg border border-neutral-200 bg-white p-2 shadow-sm dark:border-neutral-700 dark:bg-neutral-900">
          {names.map((n, idx) => (
            <li key={idx} className="truncate text-[11px] text-neutral-600 dark:text-neutral-400" title={n}>
              {n}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function productOptionLabel(p) {
  const parts = [p.name];
  if (p.status === 'DRAFT') parts.push('[Draft]');
  const stock = p.total_stock ?? 0;
  const threshold = p.low_stock_threshold || 10;
  if (stock === 0) parts.push('- Out of stock');
  else if (stock <= threshold) parts.push(`- Stock: ${stock} (Low)`);
  else parts.push(`- Stock: ${stock}`);
  return parts.join(' ');
}

export default function PurchaseRequisitions() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Action state for approve/cancel
  const [actionPending, setActionPending] = useState(null); // { id, action } or null
  const [statusFilter, setStatusFilter] = useState('');

  const [form, setForm] = useState({ productId: '' });
  const [variantItems, setVariantItems] = useState([]);
  const [generalRequestedQty, setGeneralRequestedQty] = useState('');
  const [reasonPreset, setReasonPreset] = useState('LOW_STOCK');
  const [reasonNote, setReasonNote] = useState('');

  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [productPickerOpen, setProductPickerOpen] = useState(true);

  const [variants, setVariants] = useState([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [productSuppliers, setProductSuppliers] = useState([]);

  const [variantSearch, setVariantSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('stockAsc');
  const [attrFilters, setAttrFilters] = useState({});
  const [groupByKey, setGroupByKey] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  const [bulkQuantity, setBulkQuantity] = useState('');

  const prefillHandled = useRef(false);
  const prefillVariantIdRef = useRef(null);
  const swalBase = useMemo(() => ({ ...businessSwalBase, target: '#business-swal-root' }), []);

  const fetchPRs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await procurementAPI.listPRs(params);
      setData(res?.data || res?.items || []);
      setTotalPages(res?.pagination?.pages || res?.meta?.totalPages || 1);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchPRs();
  }, [fetchPRs]);

  useEffect(() => {
    if (!showCreate) return;
    Promise.all(PROCUREMENT_STATUSES.map((s) => productsAPI.list({ limit: 200, status: s })))
      .then((results) => {
        const merged = results.flatMap((res) => res?.data || res?.products || res?.items || []);
        merged.sort((a, b) => {
          const aStock = a.total_stock ?? 0;
          const bStock = b.total_stock ?? 0;
          const aLow = aStock <= (a.low_stock_threshold || 10) ? 0 : 1;
          const bLow = bStock <= (b.low_stock_threshold || 10) ? 0 : 1;
          if (aLow !== bLow) return aLow - bLow;
          return (a.name || '').localeCompare(b.name || '');
        });
        setProducts(merged);
      })
      .catch(() => setProducts([]));
  }, [showCreate]);

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase();
    return products.filter((p) => (p.name || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q));
  }, [products, productSearch]);

  useEffect(() => {
    if (!form.productId) {
      setVariants([]);
      setProductSuppliers([]);
      setVariantItems([]);
      setGeneralRequestedQty('');
      return;
    }

    setVariantsLoading(true);
    Promise.all([
      productsAPI.getById(form.productId).then((res) => res?.data || res).catch(() => null),
      suppliersAPI.getProductSuppliers(form.productId).catch(() => ({ data: [] })),
    ])
      .then(([product, supplierRes]) => {
        const loadedVariants = product?.variants || [];
        setVariants(loadedVariants);
        setProductSuppliers(supplierRes?.data || []);

        const selectedProduct = products.find((p) => String(p.id) === String(form.productId));
        const threshold = selectedProduct?.low_stock_threshold || 10;

        if (prefillVariantIdRef.current) {
          const prefill = loadedVariants.find((v) => String(v.id) === String(prefillVariantIdRef.current));
          if (prefill && prefill.active !== 0) {
            setVariantItems([
              {
                variantId: String(prefill.id),
                requestedQuantity: String(getVariantDefaultRequestQty(prefill, threshold)),
              },
            ]);
          }
          prefillVariantIdRef.current = null;
          return;
        }

        const needsRestock = loadedVariants.filter((v) => {
          if (v.active === 0) return false;
          const state = getVariantStockState(v, threshold);
          return state === 'OUT' || state === 'LOW' || state === 'NEGATIVE';
        });

        setVariantItems(
          needsRestock.map((v) => ({
            variantId: String(v.id),
            requestedQuantity: String(getVariantDefaultRequestQty(v, threshold)),
          })),
        );
      })
      .finally(() => setVariantsLoading(false));
  }, [form.productId, products]);

  useEffect(() => {
    if (prefillHandled.current) return;
    const prefillProductId = searchParams.get('prefillProductId');
    const prefillVariantId = searchParams.get('prefillVariantId');

    if (prefillProductId) {
      prefillHandled.current = true;
      setForm((f) => ({ ...f, productId: prefillProductId }));
      setProductPickerOpen(false);
      if (prefillVariantId) prefillVariantIdRef.current = prefillVariantId;
      setShowCreate(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(form.productId)),
    [products, form.productId],
  );
  const preferredSupplier = useMemo(
    () => productSuppliers.find((s) => s.is_preferred),
    [productSuppliers],
  );
  const productThreshold = selectedProduct?.low_stock_threshold || 10;

  const variantItemsMap = useMemo(() => selectedMap(variantItems), [variantItems]);
  const summary = useMemo(
    () => calcSummary(variants, variantItems, productThreshold),
    [variants, variantItems, productThreshold],
  );

  const effectiveReason = useMemo(
    () => reasonToText(reasonPreset, reasonNote),
    [reasonPreset, reasonNote],
  );

  const isNoVariantMode = form.productId && variants.length === 0;
  const normalizedGeneralQty = normalizeRequestedQty(generalRequestedQty);
  const hasValidVariantItems = summary.selectedCount > 0 && summary.invalidQtyCount === 0 && summary.totalUnits > 0;
  const hasValidGeneralQty = isNoVariantMode && Number(normalizedGeneralQty) >= 1;
  const canSubmit = Boolean(form.productId) && (hasValidVariantItems || hasValidGeneralQty);

  const resetSelectionUiState = useCallback(() => {
    setVariantSearch('');
    setStockFilter('all');
    setSortBy('stockAsc');
    setAttrFilters({});
    setGroupByKey(null);
    setIncludeInactive(false);
    setCollapsedGroups(new Set());
    setBulkQuantity('');
  }, []);

  const handleCloseCreate = () => {
    setShowCreate(false);
    setShowConfirm(false);
    setCreateError(null);
    setForm({ productId: '' });
    setVariantItems([]);
    setGeneralRequestedQty('');
    setProductSearch('');
    setProductPickerOpen(true);
    setReasonPreset('LOW_STOCK');
    setReasonNote('');
    resetSelectionUiState();
  };

  const toggleVariant = (variantId) => {
    setVariantItems((prev) => {
      const existing = prev.find((i) => String(i.variantId) === String(variantId));
      if (existing) return prev.filter((i) => String(i.variantId) !== String(variantId));
      const v = variants.find((item) => String(item.id) === String(variantId));
      if (!v || v.active === 0) return prev;
      const qty = getVariantDefaultRequestQty(v, productThreshold);
      return [...prev, { variantId: String(variantId), requestedQuantity: String(qty) }];
    });
  };

  const updateVariantQty = (variantId, rawQty) => {
    setVariantItems((prev) =>
      prev.map((i) => (String(i.variantId) === String(variantId) ? { ...i, requestedQuantity: String(rawQty) } : i)),
    );
  };

  const selectAllVariants = () => {
    setVariantItems(
      variants
        .filter((v) => includeInactive || v.active !== 0)
        .map((v) => ({
          variantId: String(v.id),
          requestedQuantity: String(getVariantDefaultRequestQty(v, productThreshold)),
        })),
    );
  };

  const selectLowStockVariants = () => {
    setVariantItems(
      variants
        .filter((v) => {
          if (!includeInactive && v.active === 0) return false;
          const state = getVariantStockState(v, productThreshold);
          return state === 'OUT' || state === 'LOW' || state === 'NEGATIVE';
        })
        .map((v) => ({
          variantId: String(v.id),
          requestedQuantity: String(getVariantDefaultRequestQty(v, productThreshold)),
        })),
    );
  };

  const clearVariantSelection = () => setVariantItems([]);

  const applyBulkQuantity = () => {
    const normalized = normalizeRequestedQty(bulkQuantity);
    if (!normalized) return;
    setVariantItems((prev) => prev.map((i) => ({ ...i, requestedQuantity: String(normalized) })));
  };

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const handleAttrFilterChange = useCallback((key, value) => {
    setAttrFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePRAction = useCallback(async (id, action) => {
    if (actionPending?.id === id) return;

    const isApprove = action === 'approve';
    const prLabel = getPRLabel(data.find((row) => row.id === id) || { id });
    const { isConfirmed } = await Swal.fire({
      ...swalBase,
      icon: isApprove ? 'question' : 'warning',
      title: isApprove ? 'Approve Purchase Requisition' : 'Cancel Purchase Requisition',
      html: `<div class="text-left text-sm space-y-1"><p><strong>PR:</strong> ${prLabel}</p><p>${
        isApprove ? 'This requisition will move to <strong>APPROVED</strong>.' : 'This requisition will move to <strong>CANCELLED</strong> and cannot be undone.'
      }</p></div>`,
      showCancelButton: true,
      confirmButtonText: isApprove ? 'Approve' : 'Cancel Requisition',
      cancelButtonText: 'Keep Submitted',
      reverseButtons: true,
    });
    if (!isConfirmed) return;

    setActionPending({ id, action });
    try {
      if (isApprove) await procurementAPI.approvePR(id);
      else await procurementAPI.cancelPR(id);

      await fetchPRs();
      await Swal.fire({
        ...swalBase,
        icon: 'success',
        title: isApprove ? `${prLabel} approved` : `${prLabel} cancelled`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (e) {
      const detail = e?.response?.data?.message || e?.message || `Failed to ${action} ${prLabel}`;
      await Swal.fire({
        ...swalBase,
        icon: 'error',
        title: isApprove ? 'Approve failed' : 'Cancel failed',
        html: detail,
        confirmButtonText: 'OK',
      });
    } finally {
      setActionPending(null);
    }
  }, [actionPending?.id, data, fetchPRs, swalBase]);

  const handleApprove = (id) => handlePRAction(id, 'approve');
  const handleCancel = (id) => handlePRAction(id, 'cancel');

  const handleCreate = async () => {
    setSubmitting(true);
    setCreateError(null);
    try {
      const validItems = variantItems
        .map((i) => ({
          variantId: Number(i.variantId),
          requestedQuantity: Number(normalizeRequestedQty(i.requestedQuantity)),
        }))
        .filter((i) => i.variantId && i.requestedQuantity >= 1);

      const payload = {
        productId: Number(form.productId),
        reason: effectiveReason || null,
      };

      if (validItems.length > 0) {
        payload.items = validItems;
        payload.requestedQuantity = validItems.reduce((sum, i) => sum + i.requestedQuantity, 0);
      } else {
        payload.requestedQuantity = Number(normalizeRequestedQty(generalRequestedQty));
      }

      await procurementAPI.createPR(payload);
      handleCloseCreate();
      fetchPRs();
    } catch (e) {
      setCreateError(e?.message || 'Failed to create purchase requisition. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const rows = data.map((r) => {
    const isPending = actionPending?.id === r.id;
    const pendingAction = actionPending?.action;
    return {
      ...r,
      _actions:
        r.status === 'SUBMITTED' ? (
          <div className="flex gap-1">
            <button
              onClick={() => handleApprove(r.id)}
              disabled={isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:bg-emerald-950 dark:text-emerald-300 dark:hover:bg-emerald-900"
            >
              {isPending && pendingAction === 'approve' && <Loader2 className="h-3 w-3 animate-spin" />}
              Approve
            </button>
            <button
              onClick={() => handleCancel(r.id)}
              disabled={isPending}
              className="inline-flex items-center gap-1 rounded-lg bg-neutral-100 px-2.5 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-200 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700"
            >
              {isPending && pendingAction === 'cancel' && <Loader2 className="h-3 w-3 animate-spin" />}
              Cancel
            </button>
          </div>
        ) : null,
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Procurement" title="Purchase Requisitions" subtitle="Request stock replenishment for products">
        <BtnPrimary onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> New PR
        </BtnPrimary>
      </PageHeader>

      <Panel
        title="All Requisitions"
        right={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-nowrap sm:items-center">
            <ShopSelect
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
              options={PR_STATUS_OPTIONS}
              ariaLabel="Filter purchase requisitions by status"
              align="start"
              minPanelWidth={160}
              triggerClassName="min-h-8 w-40 rounded-lg px-2.5 py-1.5 text-xs"
              panelClassName="py-1"
              optionClassName="text-xs"
            />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search PR #, product, reason..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="rounded-lg border border-neutral-200 bg-white py-1.5 pl-8 pr-3 text-xs outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
              />
            </div>
          </div>
        }
      >
        <DataTable
          columns={COLUMNS}
          data={rows}
          loading={loading}
          emptyMessage="No purchase requisitions yet. Click 'New PR' to request stock."
          sortField={sortField}
          sortDir={sortDir}
          onSort={(f, d) => {
            setSortField(f);
            setSortDir(d);
            setPage(1);
          }}
        />
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </Panel>

      <FormModal
        open={showCreate}
        onClose={handleCloseCreate}
        title="Create Purchase Requisition"
        size="xl"
        footerSticky
        disableBackdropClose
        footer={
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-neutral-500 dark:text-neutral-400">
              {showConfirm
                ? 'Review the request before creating it.'
                : form.productId
                  ? `${summary.selectedCount || (hasValidGeneralQty ? 1 : 0)} selection${summary.selectedCount === 1 ? '' : 's'} · ${summary.selectedCount > 0 ? summary.totalUnits : normalizedGeneralQty || 0} total units`
                  : 'Choose a product to begin.'}
            </div>
            <div className="flex items-center gap-2">
              {showConfirm ? (
                <>
                  <BtnSecondary onClick={() => { setShowConfirm(false); setCreateError(null); }}>Back</BtnSecondary>
                  <BtnPrimary onClick={handleCreate} disabled={submitting || !canSubmit}>
                    {submitting ? 'Creating...' : 'Confirm & Create PR'}
                  </BtnPrimary>
                </>
              ) : (
                <>
                  <BtnSecondary onClick={handleCloseCreate}>Cancel</BtnSecondary>
                  <BtnPrimary onClick={() => setShowConfirm(true)} disabled={!canSubmit}>Review PR</BtnPrimary>
                </>
              )}
            </div>
          </div>
        }
      >
        <div className="mb-5 grid grid-cols-3 gap-1 rounded-xl border border-neutral-200 bg-neutral-50 p-1 dark:border-neutral-700 dark:bg-neutral-800/40">
          {[
            { number: 1, label: 'Product' },
            { number: 2, label: 'Variants & details' },
            { number: 3, label: 'Review' },
          ].map((step) => {
            const activeStep = showConfirm ? 3 : form.productId ? 2 : 1;
            const complete = step.number < activeStep;
            const active = step.number === activeStep;
            return (
              <div key={step.number} className={`flex min-w-0 items-center justify-center gap-2 rounded-lg px-2 py-2 text-xs font-semibold transition ${active ? 'bg-neutral-900 text-white shadow-sm dark:bg-neutral-100 dark:text-neutral-900' : complete ? 'text-emerald-700 dark:text-emerald-300' : 'text-neutral-400 dark:text-neutral-500'}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] ${active ? 'bg-white/15 dark:bg-black/10' : complete ? 'bg-emerald-100 dark:bg-emerald-950/60' : 'bg-neutral-200 dark:bg-neutral-700'}`}>
                  {complete ? <Check className="h-3 w-3" /> : step.number}
                </span>
                <span className="truncate">{step.label}</span>
              </div>
            );
          })}
        </div>

        {showConfirm ? (
          <div className="space-y-4">
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
              <h3 className="mb-3 text-sm font-semibold text-blue-900 dark:text-blue-200">Review Before Submitting</h3>

              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">Product</dt>
                  <dd className="truncate font-medium text-blue-900 dark:text-blue-100">{selectedProduct?.name || '-'}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">Variants</dt>
                  <dd className="font-medium text-blue-900 dark:text-blue-100">{summary.selectedCount}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">Total Units</dt>
                  <dd className="font-semibold text-blue-900 dark:text-blue-100">
                    {summary.selectedCount > 0 ? summary.totalUnits : normalizeRequestedQty(generalRequestedQty) || 0}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">Reason</dt>
                  <dd className="truncate font-medium text-blue-900 dark:text-blue-100" title={effectiveReason || '-'}>{effectiveReason || '-'}</dd>
                </div>
              </div>

              {preferredSupplier && (
                <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                  Preferred supplier: <strong>{preferredSupplier.name}</strong>
                </p>
              )}

              {summary.selectedCount > 0 && (
                <div className="mt-3 border-t border-blue-200 pt-3 dark:border-blue-800">
                  <h4 className="mb-2 text-xs font-semibold text-blue-700 dark:text-blue-300">Variant Breakdown</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-blue-200 text-left text-[10px] font-semibold uppercase tracking-wider text-blue-600 dark:border-blue-700 dark:text-blue-400">
                          <th className="pb-1.5 pr-3">Variant</th>
                          <th className="pb-1.5 pr-3">SKU</th>
                          <th className="pb-1.5 pr-3 text-right">Stock</th>
                          <th className="pb-1.5 text-right">Requested</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-blue-100 dark:divide-blue-800/50">
                        {variantItems.map((item) => {
                          const variant = variants.find((v) => String(v.id) === String(item.variantId));
                          const stockState = variant ? getVariantStockState(variant, productThreshold) : null;
                          const stockCls =
                            stockState === 'OUT' || stockState === 'NEGATIVE'
                              ? 'text-red-600 dark:text-red-400'
                              : stockState === 'LOW'
                              ? 'text-amber-600 dark:text-amber-400'
                              : '';
                          return (
                            <tr key={item.variantId}>
                              <td className="py-1.5 pr-3 font-medium text-blue-900 dark:text-blue-100">
                                {variant ? getVariantLabel(variant) || `Variant #${variant.id}` : `Variant #${item.variantId}`}
                              </td>
                              <td className="py-1.5 pr-3 font-mono text-[11px] text-blue-700 dark:text-blue-300">{variant?.sku || '-'}</td>
                              <td className={`py-1.5 pr-3 text-right tabular-nums ${stockCls}`}>{variant?.quantity ?? '-'}</td>
                              <td className="py-1.5 text-right tabular-nums font-semibold text-blue-900 dark:text-blue-100">
                                {normalizeRequestedQty(item.requestedQuantity) || 0}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-blue-200 font-semibold text-blue-900 dark:border-blue-700 dark:text-blue-100">
                          <td className="pt-2" colSpan={3}>Total</td>
                          <td className="pt-2 text-right tabular-nums">{summary.totalUnits} units</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {createError && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                <strong>Submission failed:</strong> {createError}
              </div>
            )}

            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              Created PRs start in <strong>SUBMITTED</strong> and require approval before RFQ/PO progression.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            <section className="space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">1. Product</h3>
              {selectedProduct && !productPickerOpen ? (
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/20">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300">
                    <Package className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">{selectedProduct.name}</p>
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">{selectedProduct.status || 'Product'}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                      {selectedProduct.sku || 'No SKU'} · Stock: {selectedProduct.total_stock ?? 0}
                    </p>
                  </div>
                  <button type="button" onClick={() => setProductPickerOpen(true)} className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-600 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800">
                    Change product
                  </button>
                </div>
              ) : (
                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/40">
                  <label className="mb-2 flex items-center gap-1 text-xs font-medium text-neutral-700 dark:text-neutral-300">
                    Choose product <span className="text-red-500">*</span>
                    <span className="ml-auto text-[10px] font-normal text-neutral-400" title="Choose product for restock planning">
                      <Info className="inline h-3 w-3" /> Includes Draft + Listed products
                    </span>
                  </label>

                  <div className="grid gap-2 md:grid-cols-[minmax(220px,1fr)_minmax(280px,1.4fr)]">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
                      <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search name or SKU..." className={inputCls + ' pl-8 text-xs'} />
                    </div>
                    <ShopSelect
                      value={form.productId}
                      onValueChange={(value) => {
                        setForm((current) => ({ ...current, productId: value }));
                        setVariantItems([]);
                        setGeneralRequestedQty('');
                        resetSelectionUiState();
                        setProductPickerOpen(!value);
                      }}
                      options={[{ value: "", label: "Select a product..." }, ...filteredProducts.map((product) => ({ value: product.id, label: productOptionLabel(product) }))]}
                      ariaLabel="Product"
                      align="start"
                      minPanelWidth={320}
                      triggerClassName="min-h-10 w-full rounded-xl px-3 py-2 text-sm"
                      panelClassName="py-1"
                    />
                  </div>

                  {products.length > 0 && filteredProducts.length === 0 && productSearch && (
                    <p className="mt-2 text-xs text-neutral-500">No products match "{productSearch}"</p>
                  )}
                </div>
              )}
            </section>

            {form.productId && (
              <section className="space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">2. Variants &amp; quantities</h3>

                {variantsLoading ? (
                  <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800/40">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading variants...
                  </div>
                ) : (
                  <div className="space-y-3">
                      <VariantList
                        variants={variants}
                        selectedMap={variantItemsMap}
                        productThreshold={productThreshold}
                        search={variantSearch}
                        onSearchChange={setVariantSearch}
                        stockFilter={stockFilter}
                        onStockFilterChange={setStockFilter}
                        sortBy={sortBy}
                        onSortByChange={setSortBy}
                        attrFilters={attrFilters}
                        onAttrFilterChange={handleAttrFilterChange}
                        groupByKey={groupByKey}
                        onGroupByKeyChange={setGroupByKey}
                        includeInactive={includeInactive}
                        onIncludeInactiveChange={setIncludeInactive}
                        collapsedGroups={collapsedGroups}
                        onToggleGroup={toggleGroup}
                        onToggleVariant={toggleVariant}
                        onUpdateVariantQty={updateVariantQty}
                        onSelectAll={selectAllVariants}
                        onSelectLowStock={selectLowStockVariants}
                        onClearSelection={clearVariantSelection}
                        selectedCount={summary.selectedCount}
                        bulkQuantity={bulkQuantity}
                        onBulkQuantityChange={setBulkQuantity}
                        onApplyBulk={applyBulkQuantity}
                      />

                    <SummaryPanel
                      selectedCount={summary.selectedCount}
                      totalUnits={summary.totalUnits}
                      invalidQtyCount={summary.invalidQtyCount}
                      outSelected={summary.outSelected}
                      lowSelected={summary.lowSelected}
                      sufficientSelected={summary.sufficientSelected}
                      negativeStockSelected={summary.negativeStockSelected}
                      productSuppliers={productSuppliers}
                      preferredSupplier={preferredSupplier}
                    />
                  </div>
                )}

                {isNoVariantMode && (
                  <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/40">
                    <label className="mb-1 block text-xs font-medium text-neutral-700 dark:text-neutral-300">Requested Quantity *</label>
                    <input
                      type="number"
                      min="1"
                      value={generalRequestedQty}
                      onChange={(e) => setGeneralRequestedQty(e.target.value)}
                      onBlur={(e) => setGeneralRequestedQty(String(normalizeRequestedQty(e.target.value) || ''))}
                      className={inputCls}
                      placeholder="Set requested quantity for this product"
                    />
                    <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                      This product currently has no active variants. PR will be created with product-level requested quantity.
                    </p>
                  </div>
                )}
              </section>
            )}

            <section className={`space-y-2 ${!form.productId ? 'opacity-60' : ''}`}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">3. Reason</h3>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-700 dark:bg-neutral-800/40">
                <div className="mb-2 flex flex-wrap gap-2">
                  {REASON_PRESETS.map((reason) => {
                    const active = reasonPreset === reason.code;
                    return (
                      <button
                        key={reason.code}
                        type="button"
                        disabled={!form.productId}
                        onClick={() => setReasonPreset(reason.code)}
                        className={[
                          'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                          active
                            ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300'
                            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700',
                        ].join(' ')}
                      >
                        {reason.label}
                      </button>
                    );
                  })}
                </div>

                <textarea
                  rows={3}
                  disabled={!form.productId}
                  value={reasonNote}
                  onChange={(e) => setReasonNote(e.target.value)}
                  className={inputCls}
                  placeholder={reasonPreset === 'OTHER' ? 'Provide reason details...' : 'Optional notes for approver context'}
                />
              </div>
            </section>
          </div>
        )}
      </FormModal>
    </div>
  );
}
